import PropTypes from 'prop-types';
import { useState } from 'react';
import Button from './ui/Button';
import { submitFormPayload } from '../utils/submitForm';

export default function SubscribeForm({ tone = 'light', className = '' }) {
  const [formValues, setFormValues] = useState({ firstName: '', lastName: '', email: '' });
  const [status, setStatus] = useState(null);

  const isValidEmail = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
  const isValid =
    formValues.firstName.trim().length > 0 &&
    formValues.lastName.trim().length > 0 &&
    isValidEmail(formValues.email);

  const buttonVariant = tone === 'dark' ? 'secondary' : 'primary';

  const buttonToneClass =
    tone === 'dark' ? 'focus:ring-offset-cam-blue' : 'focus:ring-offset-white';

  function handleChange(event) {
    const { name, value } = event.target;
    setFormValues((prev) => ({ ...prev, [name]: value }));
    if (status) {
      setStatus(null);
    }
  }

  async function handleSubmit(event) {
    event.preventDefault();
    if (!isValid) {
      setStatus({ type: 'error', message: 'Please complete all required fields with a valid email address.' });
      return;
    }

    setStatus({ type: 'loading', message: 'Submitting…' });

    const response = await submitFormPayload({
      formName: 'Newsletter – Email Insights Signup',
      firstName: formValues.firstName,
      lastName: formValues.lastName,
      email: formValues.email
    });

    if (response?.success) {
      setStatus({ type: 'success', message: "Thanks for subscribing. We'll send you new insights when they're available." });
      setFormValues({ firstName: '', lastName: '', email: '' });
    } else {
      console.error('Form submit failed', response);
      setStatus({ type: 'error', message: 'Something went wrong submitting your request. Please try again in a moment.' });
    }
  }

  return (
    <form
      onSubmit={handleSubmit}
      className={`space-y-3 ${className}`.trim()}
      noValidate
      aria-describedby={status ? 'subscribe-status' : undefined}
    >
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <input
          required
          name="firstName"
          value={formValues.firstName}
          onChange={handleChange}
          placeholder="First name"
          aria-label="First name"
          className="w-full rounded-lg border border-slate-300 px-3 py-3 md:py-2 text-base md:text-sm text-slate-700 placeholder:text-slate-400 focus:border-cam-blue focus:outline-none focus:ring-2 focus:ring-cam-blue"
        />
        <input
          required
          name="lastName"
          value={formValues.lastName}
          onChange={handleChange}
          placeholder="Last name"
          aria-label="Last name"
          className="w-full rounded-lg border border-slate-300 px-3 py-3 md:py-2 text-base md:text-sm text-slate-700 placeholder:text-slate-400 focus:border-cam-blue focus:outline-none focus:ring-2 focus:ring-cam-blue"
        />
      </div>
      <div className="flex flex-col gap-3 sm:flex-row">
        <input
          required
          type="email"
          name="email"
          value={formValues.email}
          onChange={handleChange}
          placeholder="name@email.com"
          aria-label="Email"
          className="w-full rounded-lg border border-slate-300 px-3 py-3 md:py-2 text-base md:text-sm text-slate-700 placeholder:text-slate-400 focus:border-cam-blue focus:outline-none focus:ring-2 focus:ring-cam-blue"
        />
        <Button
          type="submit"
          variant={buttonVariant}
          className={`w-full sm:w-auto ${buttonToneClass}`.trim()}
          disabled={!isValid || status?.type === 'loading'}
        >
          {status?.type === 'loading' ? 'Submitting…' : 'Subscribe'}
        </Button>
      </div>
      {status && (
        <p
          id="subscribe-status"
          className={`text-base md:text-sm ${
            status.type === 'error'
              ? 'text-red-600'
              : tone === 'dark'
              ? 'text-cam-ivory'
              : 'text-cam-blue'
          }`}
          role={status.type === 'error' ? 'alert' : 'status'}
        >
          {status.message}
        </p>
      )}
    </form>
  );
}

SubscribeForm.propTypes = {
  tone: PropTypes.oneOf(['light', 'dark']),
  className: PropTypes.string
};
